/**
 * CompleteHistoryTab - Shows all interactions for a customer by ID and email.
 *
 * Displays a chronological list of all interactions (both inbound and outbound)
 * for the current customer, starting at the bottom (most recent) and allowing
 * scroll up to view older interactions.
 *
 * Queries by both customer_id AND the email address to ensure complete coverage.
 *
 * Task 3201b: each row can be expanded inline to reveal the full message body
 * (HTML or plain text), rendered using the same `HtmlEmailFrame` pipeline that
 * the `Message` tab uses for the currently-selected interaction.
 */

import {
  useEffect,
  useState,
  useRef,
  useCallback,
  type CSSProperties,
  type ReactNode,
} from 'react'
import { format, formatDistanceToNow } from 'date-fns'
import { unixSecondsToDate, formatUnixSeconds } from '../dateUtils'
import { Mail, ArrowDown, ArrowUp, Paperclip, Loader2, Image } from 'lucide-react'
import { api } from '@/lib/api'
import { useInboxStore } from '../store'
import type { Interaction, InteractionClassification, InteractionListResponse, MessageAttachment } from '../types'
import { ClassificationBadge } from './ClassificationBadge'
import { getCategory } from '../classification'
import {
  HtmlEmailFrame,
  AttachmentChip,
  AttachmentPreviewModal,
  formatSize,
} from './MessageRenderers'
import { getMessageForInteraction } from './MessageView'

// ── Props ────────────────────────────────────────────────────────────────────

interface CompleteHistoryTabProps {
  customerId?: string
  /** Customer email address. Optional — when absent, queries by customerId only. */
  customerEmail?: string
  currentInteractionId: string
}

// ── Component ────────────────────────────────────────────────────────────────

export function CompleteHistoryTab({
  customerId,
  customerEmail,
  currentInteractionId,
}: CompleteHistoryTabProps): ReactNode {
  const [interactions, setInteractions] = useState<Interaction[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    let mounted = true

    async function loadAllInteractions() {
      setLoading(true)
      setError(null)

      try {
        // Use the dedicated customer-history endpoint which queries
        // Firestore by customer_email, email.from, email.to, and
        // author_id server-side.  No client-side filtering needed.
        const params: Record<string, string> = {}
        if (customerEmail) params.customer_email = customerEmail
        if (customerId) params.customer_id = customerId

        const response = await api.get<InteractionListResponse>(
          '/v1/interactions/by-customer',
          params,
        )

        const allInteractions = [...response.data]

        // Make sure the current interaction is always included
        const current = useInboxStore.getState().interactions.find(
          (i) => i.id === currentInteractionId,
        )
        if (current) {
          allInteractions.push(current)
        }

        // Deduplicate by interaction ID
        const uniqueMap = new Map<string, Interaction>()
        for (const interaction of allInteractions) {
          uniqueMap.set(interaction.id, interaction)
        }

        // Sort chronologically (oldest first; bottom = most recent)
        const sorted = Array.from(uniqueMap.values()).sort(
          (a, b) => a.received_at - b.received_at,
        )

        if (mounted) {
          setInteractions(sorted)
          setLoading(false)
        }
      } catch (err) {
        if (mounted) {
          setError(err instanceof Error ? err.message : String(err))
          setLoading(false)
        }
      }
    }

    loadAllInteractions()

    return () => {
      mounted = false
    }
  }, [customerId, customerEmail, currentInteractionId])

  // Scroll to bottom (most recent) AFTER rows have rendered.
  // We use a double-rAF + setTimeout fallback so the scroll happens AFTER the
  // browser has finished laying out the rows — otherwise scrollHeight would be
  // computed before content is rendered and scrolling would be a no-op.
  useEffect(() => {
    if (loading || interactions.length === 0 || !scrollContainerRef.current) {
      return
    }
    const el = scrollContainerRef.current
    const scrollToBottom = () => {
      el.scrollTop = el.scrollHeight
    }
    // Double-rAF: first frame schedules layout, second frame runs after layout.
    let raf2 = 0
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(scrollToBottom)
    })
    // Fallback in case rAF runs before layout completes (rare but possible).
    const timeout = window.setTimeout(scrollToBottom, 150)
    return () => {
      cancelAnimationFrame(raf1)
      cancelAnimationFrame(raf2)
      window.clearTimeout(timeout)
    }
  }, [loading, interactions.length])

  // One-at-a-time expansion: clicking a row toggles its own expansion and
  // collapses any other row that may be open. We deliberately do NOT call
  // `selectInteraction` here — history rows are not in the main inbox list,
  // and selecting them would (a) double-fetch the same thread (once via
  // `loadInteractionDetail`, once via `loadHistoryInteractionDetail`) and
  // (b) clobber the Message tab with a row the operator did not select.
  // The "Current" highlight cue is driven by `currentInteractionId`, not by
  // store-level selectedId, so no visual regression results.
  const handleToggleExpand = useCallback((id: string) => {
    setExpandedId((prev) => (prev === id ? null : id))
  }, [])

  if (loading) {
    return (
      <div style={styles.stateContainer}>
        <Loader2
          size={20}
          style={{ color: 'var(--text-muted)', animation: 'spin 1s linear infinite' }}
        />
        <p style={styles.stateText}>Loading complete interaction history...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div style={styles.stateContainer}>
        <p style={styles.errorText}>Failed to load interaction history</p>
        <p style={styles.errorDetail}>{error}</p>
      </div>
    )
  }

  if (interactions.length === 0) {
    return (
      <div style={styles.stateContainer}>
        <Mail size={32} style={{ color: 'var(--text-muted)', opacity: 0.5 }} />
        <p style={styles.stateText}>No interactions found</p>
        <p style={styles.stateSubtext}>
          No interactions found for {customerId || customerEmail ? (customerId ? 'this customer' : customerEmail) : 'this customer'}
        </p>
      </div>
    )
  }

  return (
    <div style={styles.container}>
      {/* Header with count and scroll hint */}
      <div style={styles.header}>
        <span style={styles.countBadge}>
          {interactions.length} interaction{interactions.length !== 1 ? 's' : ''}
        </span>
        <span style={styles.headerHint}>
          Scroll up for older messages
        </span>
      </div>

      {/* Scrollable area — flex:1 + overflow:auto is more reliable than the
          absolute-positioning trick since it doesn't require the containing
          block to have a definite pixel height resolved from above. */}
      <div ref={scrollContainerRef} style={styles.scrollWrapper}>
        <div style={styles.list}>
          {interactions.map((interaction) => (
            <InteractionRow
              key={interaction.id}
              interaction={interaction}
              isActive={interaction.id === currentInteractionId}
              expanded={expandedId === interaction.id}
              onToggleExpand={() => handleToggleExpand(interaction.id)}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

// ── InteractionRow Sub-Component ─────────────────────────────────────────────

interface InteractionRowProps {
  interaction: Interaction
  isActive: boolean
  expanded: boolean
  onToggleExpand: () => void
}

function InteractionRow({
  interaction,
  isActive,
  expanded,
  onToggleExpand,
}: InteractionRowProps): ReactNode {
  const isInbound = interaction.direction === 'inbound'
  const directionIcon = isInbound ? (
    <ArrowDown size={14} style={{ color: 'var(--accent-primary)' }} />
  ) : (
    <ArrowUp size={14} style={{ color: 'var(--success)' }} />
  )

  // Defensive: `received_at` may be null/undefined/NaN on malformed rows.
  // date-fns format/formatDistanceToNow throw "Invalid time value" on an
  // Invalid Date and would crash the history list.
  const date = unixSecondsToDate(interaction.received_at)

  return (
    <div style={styles.rowWrapper}>
      <button
        type="button"
        onClick={onToggleExpand}
        aria-expanded={expanded}
        style={{
          ...styles.row,
          ...(isActive ? styles.rowActive : {}),
          ...(expanded ? styles.rowExpanded : {}),
        }}
      >
        {/* Direction indicator */}
        <div style={styles.rowIcon}>{directionIcon}</div>

        {/* Content */}
        <div style={styles.rowContent}>
          {/* Top line: subject + active badge */}
          <div style={styles.rowHeader}>
            <span style={styles.rowSubject} title={interaction.subject}>
              {interaction.subject || '(no subject)'}
            </span>
            {interaction.has_attachment && (
              <Paperclip
                size={12}
                style={{ color: 'var(--text-muted)', flexShrink: 0 }}
              />
            )}
            {isActive && <span style={styles.activeLabel}>Current</span>}
          </div>

          {/* Meta line: from · date · classification */}
          <div style={styles.rowMeta}>
            <span style={styles.rowFrom} title={interaction.from}>
              {isInbound ? '← ' : '→ '}
              {interaction.from_name || interaction.from}
            </span>
            <span style={styles.rowSeparator}>·</span>
            <span
              style={styles.rowDate}
              title={date ? format(date, 'MMM d, yyyy h:mm a') : ''}
            >
              {date ? formatDistanceToNow(date, { addSuffix: true }) : '—'}
            </span>
            <ClassificationBadge
              classification={getCategory(interaction) as InteractionClassification}
              size="sm"
            />
          </div>

          {/* Preview text (collapsed only — full body shows below when expanded) */}
          {!expanded && interaction.preview && (
            <p style={styles.rowPreview} title={interaction.preview}>
              {interaction.preview}
            </p>
          )}
        </div>
      </button>

      {expanded && <ExpandedMessageBody interaction={interaction} />}
    </div>
  )
}

// ── ExpandedMessageBody ──────────────────────────────────────────────────────

interface ExpandedMessageBodyProps {
  interaction: Interaction
}

/**
 * Lazy-loads the full thread for a single history row and renders the matching
 * `Message` body using the shared `HtmlEmailFrame`. Reads from `detailCache`
 * on subsequent expansions so the network is hit at most once per id.
 */
function ExpandedMessageBody({ interaction }: ExpandedMessageBodyProps): ReactNode {
  const detail = useInboxStore((s) => s.detailCache.get(interaction.id))
  const loadHistoryInteractionDetail = useInboxStore(
    (s) => s.loadHistoryInteractionDetail,
  )

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [previewAttachment, setPreviewAttachment] = useState<MessageAttachment | null>(null)

  const fetchDetail = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      await loadHistoryInteractionDetail(interaction)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setLoading(false)
    }
  }, [loadHistoryInteractionDetail, interaction])

  useEffect(() => {
    // Skip the network call if a non-stale entry already exists in the cache.
    // The 30s TTL mirrors loadInteractionDetail / loadHistoryInteractionDetail.
    if (detail && Date.now() - detail.lastFetchedAt < 30000) {
      return
    }
    fetchDetail()
    // We intentionally key on interaction.id only: fetchDetail is stable per
    // interaction and re-running it on every detail-cache mutation would
    // defeat the cache.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interaction.id])

  if (loading && !detail) {
    return (
      <div style={styles.expansion}>
        <div style={styles.expansionLoading}>
          <Loader2
            size={14}
            style={{ color: 'var(--text-muted)', animation: 'spin 1s linear infinite' }}
          />
          <span>Loading message…</span>
        </div>
      </div>
    )
  }

  if (error && !detail) {
    return (
      <div style={styles.expansion}>
        <div style={styles.expansionError}>
          <span>Failed to load message: {error}</span>
          <button type="button" style={styles.retryBtn} onClick={fetchDetail}>
            Retry
          </button>
        </div>
      </div>
    )
  }

  const message = getMessageForInteraction(detail?.messages ?? [], interaction)

  if (!message) {
    return (
      <div style={styles.expansion}>
        <p style={styles.expansionEmpty}>No message body available for this interaction.</p>
      </div>
    )
  }

  const htmlContent = message.html_body || message.body_html || ''
  const textContent = message.body || message.body_text || ''
  const hasHtml = !!htmlContent
  const attachments = message.attachments ?? []
  const imageAttachments = attachments.filter(
    (att) => att.content_type?.startsWith('image/') && att.url,
  )

  return (
    <div style={styles.expansion}>
      {/* Sender / timestamp header */}
      <div style={styles.expansionHeader}>
        <span style={styles.expansionSender}>{message.from}</span>
        <span style={styles.expansionTimestamp}>
          {formatUnixSeconds(message.received_at, 'MMM d, yyyy h:mm a')}
        </span>
      </div>

      {/* Attachments chip bar */}
      {attachments.length > 0 && (
        <div style={styles.expansionAttachmentsBar}>
          <div style={styles.expansionAttachmentsHeader}>
            <Paperclip size={12} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
            <span style={styles.expansionAttachmentsLabel}>
              {attachments.length} attachment{attachments.length > 1 ? 's' : ''}
            </span>
          </div>
          <div style={styles.expansionAttachmentsList}>
            {attachments.map((att) => (
              <AttachmentChip key={att.id} attachment={att} onPreview={setPreviewAttachment} />
            ))}
          </div>
        </div>
      )}

      {/* Body */}
      {hasHtml ? (
        <HtmlEmailFrame
          html={htmlContent}
          style={{ minHeight: '200px', height: '40vh' }}
        />
      ) : (
        <pre style={styles.expansionPlainText}>{textContent}</pre>
      )}

      {/* Inline image attachments */}
      {imageAttachments.length > 0 && (
        <div style={styles.expansionInlineImages}>
          <div style={styles.expansionInlineImagesHeader}>
            <Image size={12} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
            <span style={styles.expansionAttachmentsLabel}>
              {imageAttachments.length} image{imageAttachments.length > 1 ? 's' : ''}
            </span>
          </div>
          <div style={styles.expansionInlineImagesGrid}>
            {imageAttachments.map((att) => (
              <button
                key={att.id}
                type="button"
                style={styles.expansionInlineImageCard}
                onClick={() => setPreviewAttachment(att)}
                title={`${att.filename} — click to preview`}
              >
                <img src={att.url} alt={att.filename} style={styles.expansionInlineImageImg} />
                <span style={styles.expansionInlineImageCaption}>
                  {att.filename}
                  <span style={styles.expansionInlineImageSize}>{formatSize(att.size)}</span>
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {previewAttachment && (
        <AttachmentPreviewModal
          attachment={previewAttachment}
          onClose={() => setPreviewAttachment(null)}
        />
      )}
    </div>
  )
}

// ── Styles ───────────────────────────────────────────────────────────────────

const styles: Record<string, CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    width: '100%',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 16px',
    borderBottom: '1px solid var(--border-subtle)',
    background: 'var(--bg-tertiary)',
    flexShrink: 0,
    position: 'relative',
    zIndex: 1,
  },
  scrollWrapper: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    overflowX: 'hidden',
    width: '100%',
  },
  countBadge: {
    padding: '2px 8px',
    fontSize: '12px',
    fontWeight: 500,
    color: 'var(--text-secondary)',
    background: 'var(--bg-secondary)',
    borderRadius: '100px',
  },
  headerHint: {
    fontSize: '11px',
    color: 'var(--text-muted)',
    fontStyle: 'italic',
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    padding: '12px',
    width: '100%',
    boxSizing: 'border-box',
  },
  rowWrapper: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  // NOTE: border / borderRadius use the LONGHAND properties (not the shorthand)
  // because `rowExpanded` toggles `borderBottomWidth: 0` and zeroes the bottom
  // corner radii on the active row. React warns when shorthand and longhand
  // properties for the same value are mixed across renders, so we keep the
  // base styles purely longhand to avoid that interleaving.
  row: {
    display: 'flex',
    gap: '10px',
    padding: '10px 12px',
    background: 'var(--bg-tertiary)',
    borderStyle: 'solid',
    borderTopWidth: '1px',
    borderRightWidth: '1px',
    borderBottomWidth: '1px',
    borderLeftWidth: '1px',
    borderColor: 'var(--border-subtle)',
    borderTopLeftRadius: 'var(--radius-md)',
    borderTopRightRadius: 'var(--radius-md)',
    borderBottomLeftRadius: 'var(--radius-md)',
    borderBottomRightRadius: 'var(--radius-md)',
    transition: 'all var(--transition-fast)',
    cursor: 'pointer',
    width: '100%',
    boxSizing: 'border-box',
    textAlign: 'left',
    fontFamily: 'inherit',
    color: 'inherit',
  },
  rowActive: {
    background: 'color-mix(in srgb, var(--accent-primary) 10%, var(--bg-tertiary))',
    borderColor: 'var(--accent-primary)',
  },
  rowExpanded: {
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    borderBottomWidth: 0,
  },
  rowIcon: {
    display: 'flex',
    alignItems: 'flex-start',
    paddingTop: '2px',
    flexShrink: 0,
  },
  rowContent: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  rowHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    minWidth: 0,
  },
  rowSubject: {
    fontSize: '13px',
    fontWeight: 600,
    color: 'var(--text-primary)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    flex: 1,
    minWidth: 0,
  },
  activeLabel: {
    padding: '1px 6px',
    fontSize: '10px',
    fontWeight: 600,
    color: 'var(--accent-primary)',
    background: 'color-mix(in srgb, var(--accent-primary) 15%, transparent)',
    borderRadius: '3px',
    flexShrink: 0,
    textTransform: 'uppercase',
    letterSpacing: '0.03em',
  },
  rowMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '11px',
    color: 'var(--text-muted)',
    minWidth: 0,
    flexWrap: 'wrap',
  },
  rowFrom: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '200px',
  },
  rowSeparator: {
    flexShrink: 0,
  },
  rowDate: {
    flexShrink: 0,
  },
  rowPreview: {
    margin: 0,
    fontSize: '12px',
    color: 'var(--text-secondary)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    lineHeight: 1.4,
  },
  // Same longhand-only treatment as `row` — the expansion shares a continuous
  // border with the row above it (no top border) and only the bottom corners
  // round, so we avoid mixing shorthand `border` with longhand `borderTop`.
  expansion: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    padding: '12px',
    background: 'var(--bg-secondary)',
    borderStyle: 'solid',
    borderTopWidth: 0,
    borderRightWidth: '1px',
    borderBottomWidth: '1px',
    borderLeftWidth: '1px',
    borderColor: 'var(--border-subtle)',
    borderBottomLeftRadius: 'var(--radius-md)',
    borderBottomRightRadius: 'var(--radius-md)',
    width: '100%',
    boxSizing: 'border-box',
  },
  expansionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '10px',
    paddingBottom: '8px',
    borderBottom: '1px solid var(--border-subtle)',
  },
  expansionSender: {
    fontSize: '12px',
    fontWeight: 500,
    color: 'var(--text-primary)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    flex: 1,
    minWidth: 0,
  },
  expansionTimestamp: {
    fontSize: '11px',
    color: 'var(--text-muted)',
    flexShrink: 0,
  },
  expansionPlainText: {
    margin: 0,
    padding: '12px',
    fontSize: '13px',
    lineHeight: 1.6,
    color: '#1a1a1a',
    background: '#ffffff',
    border: '1px solid var(--border-subtle)',
    borderRadius: '6px',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
    maxHeight: '40vh',
    overflow: 'auto',
  },
  expansionAttachmentsBar: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  expansionAttachmentsHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  expansionAttachmentsLabel: {
    fontSize: '11px',
    fontWeight: 500,
    color: 'var(--text-muted)',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.04em',
  },
  expansionAttachmentsList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
  },
  expansionInlineImages: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    paddingTop: '8px',
    borderTop: '1px solid var(--border-subtle)',
  },
  expansionInlineImagesHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  expansionInlineImagesGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  expansionInlineImageCard: {
    display: 'inline-flex',
    flexDirection: 'column',
    background: 'transparent',
    border: '1px solid var(--border-subtle)',
    borderRadius: '6px',
    overflow: 'hidden',
    cursor: 'pointer',
    padding: 0,
    textAlign: 'left',
    transition: 'border-color var(--transition-fast)',
    alignSelf: 'flex-start',
    maxWidth: '100%',
  },
  expansionInlineImageImg: {
    display: 'block',
    maxWidth: '100%',
    height: 'auto',
  },
  expansionInlineImageCaption: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
    padding: '6px 10px',
    fontSize: '11px',
    fontWeight: 500,
    color: 'var(--text-secondary)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  expansionInlineImageSize: {
    fontSize: '10px',
    color: 'var(--text-muted)',
    flexShrink: 0,
  },
  expansionLoading: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '12px',
    color: 'var(--text-muted)',
    padding: '8px 4px',
  },
  expansionError: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
    fontSize: '12px',
    color: 'var(--error)',
    padding: '8px 4px',
  },
  expansionEmpty: {
    margin: 0,
    fontSize: '12px',
    color: 'var(--text-muted)',
    padding: '8px 4px',
  },
  retryBtn: {
    padding: '4px 10px',
    fontSize: '11px',
    fontWeight: 500,
    color: 'var(--text-primary)',
    background: 'var(--bg-tertiary)',
    border: '1px solid var(--border-subtle)',
    borderRadius: 'var(--radius-sm)',
    cursor: 'pointer',
    flexShrink: 0,
  },
  stateContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    minHeight: 0,
    gap: '12px',
    padding: '40px 20px',
  },
  stateText: {
    margin: 0,
    fontSize: '14px',
    fontWeight: 500,
    color: 'var(--text-secondary)',
  },
  stateSubtext: {
    margin: 0,
    fontSize: '12px',
    color: 'var(--text-muted)',
    textAlign: 'center',
  },
  errorText: {
    margin: 0,
    fontSize: '14px',
    fontWeight: 500,
    color: 'var(--error)',
  },
  errorDetail: {
    margin: 0,
    fontSize: '12px',
    color: 'var(--text-muted)',
    textAlign: 'center',
  },
}

// Add spin animation keyframes via inline style tag (one-time init)
if (typeof document !== 'undefined' && !document.getElementById('inbox-history-spin')) {
  const styleEl = document.createElement('style')
  styleEl.id = 'inbox-history-spin'
  styleEl.textContent = `
    @keyframes spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
  `
  document.head.appendChild(styleEl)
}

export default CompleteHistoryTab
